<script setup lang="ts">
  /**
   * The only vertical nav in the product: authenticated client area, sits below the app header
   * and spans to the bottom of the content area. Marketing pages keep BfHeader.
   *
   * Must be placed inside BfConsoleShell — the shell is the container query's container, and
   * without it the nav never becomes a drawer. Three states: expanded 220px (default),
   * `collapsed` 64px icon-only rail with a dark hover tooltip, and `open` mobile drawer over a
   * scrim. Rail is only defined above --bf-bp-console; below it the modifier is inert, so the
   * drawer is always full-label and no rail rule has to be undone.
   *
   * The nav itself never scrolls — the list does. That keeps the rail tooltips and the edge
   * toggle out of an ancestor overflow.
   *
   * Active state is authored by the caller as `current` on an item and rendered as
   * `aria-current="page"` — nothing here derives it from the URL, so server-rendered and SPA
   * consumers behave identically. The style hangs off the attribute, not a class.
   *
   * The drawer's hamburger lives in the app header, outside this component: bind `open` with
   * v-model and move focus back to it when `close` fires.
   */
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

  import type { BfConsoleNavItem } from '@/typescript/types/ui-components/BfConsoleNavI'

  import BfIconChevronLeft from '@/components/ui/icons/BfIconChevronLeft.vue'

  // The scrim is a second root node, sibling to the nav — so attrs have nowhere unambiguous to
  // land on their own. They are bound to the <nav> explicitly instead: a consumer's `id` has to
  // reach the navigation, because the app header's hamburger points `aria-controls` at it.
  defineOptions({ inheritAttrs: false })

  /** Collapse state persists under one key so the rail survives a route change. */
  const STORAGE_KEY = 'console-navigation.collapsed'

  const props = withDefaults(
    defineProps<{
      items: BfConsoleNavItem[]
      /** Rail mode: 64px, icon only. Inert below --bf-bp-console. */
      collapsed?: boolean
      /** Drawer open. Only meaningful below --bf-bp-console. */
      open?: boolean
      /** Turns the group hairlines into uppercase captions. */
      labelled?: boolean
      /** Read and write the collapse state to localStorage. Opt-in. */
      persist?: boolean
      ariaLabel?: string
    }>(),
    { collapsed: false, open: false, labelled: false, persist: false, ariaLabel: 'Main navigation' },
  )

  const emit = defineEmits<{
    'update:collapsed': [collapsed: boolean]
    'update:open': [open: boolean]
    close: []
  }>()

  const navRef = ref<HTMLElement | null>(null)

  const isLink = (item: BfConsoleNavItem): boolean => item.type !== 'group'

  /**
   * The destination attributes, one branch each — `href` is never handed to RouterLink.
   *
   * Vue merges fallthrough attrs AFTER the component's own rendered attrs, so a `:href` bound
   * alongside `:to` overrides the href RouterLink has just computed; bound to `undefined`, it
   * strips the attribute altogether. An <a> with no href cannot be opened in a new tab, copied,
   * middle-clicked or reached by keyboard, and is not announced as a link — the plain click keeps
   * working, which is what hides it.
   */
  const linkAttrs = (item: BfConsoleNavItem): Record<string, unknown> => {
    // A disabled row is inert: no destination at all, so it is neither focusable nor activatable.
    if (item.disabled) return {}
    if (item.to) return { to: item.to }
    return item.external ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' } : { href: item.href }
  }

  const toggleLabel = computed<string>(() => (props.collapsed ? 'Expand sidebar' : 'Collapse sidebar'))

  const readStored = (): boolean | null => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY)
      return stored === null ? null : stored === '1'
    } catch {
      // Private mode and blocked site data both throw; the nav just starts expanded.
      return null
    }
  }

  const writeStored = (collapsed: boolean): void => {
    try {
      window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0')
    } catch {
      // Nothing to do — the rail simply will not survive the next route change.
    }
  }

  const toggleRail = (): void => {
    const next = !props.collapsed
    emit('update:collapsed', next)
    if (props.persist) writeStored(next)
  }

  const closeDrawer = (): void => {
    emit('update:open', false)
    emit('close')
  }

  /** Closing on link activation stands in for the route change the caller is about to make. */
  const onLinkActivate = (): void => {
    if (props.open) closeDrawer()
  }

  const focusableItems = (): HTMLElement[] =>
    Array.from(navRef.value?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? [])

  const onKeydown = (event: KeyboardEvent): void => {
    if (!props.open) return

    if (event.key === 'Escape') {
      closeDrawer()
      return
    }

    if (event.key !== 'Tab') return

    const items = focusableItems()
    if (!items.length) return

    const first = items[0]
    const last = items[items.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  // Focus moves into the drawer on open; the caller returns it to the hamburger on close.
  watch(
    () => props.open,
    async open => {
      if (!open) return
      await nextTick()
      focusableItems()[0]?.focus()
    },
  )

  onMounted(() => {
    if (props.persist) {
      const stored = readStored()
      if (stored !== null && stored !== props.collapsed) emit('update:collapsed', stored)
    }

    document.addEventListener('keydown', onKeydown)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown)
  })
</script>

<template>
  <nav
    ref="navRef"
    v-bind="$attrs"
    class="console-navigation"
    :class="{ 'console-navigation--labelled-group-dividers': labelled }"
    :data-state="collapsed ? 'rail' : undefined"
    :data-drawer="open ? 'open' : undefined"
    :aria-label="ariaLabel"
  >
    <ul class="console-navigation__list" role="list">
      <template v-for="(item, index) in items" :key="`${item.label}-${index}`">
        <li v-if="!isLink(item)" class="console-navigation__group-divider" aria-hidden="true">
          <span class="console-navigation__group-label">{{ item.label }}</span>
        </li>

        <li v-else class="console-navigation__item">
          <component
            :is="!item.disabled && item.to ? 'RouterLink' : 'a'"
            class="console-navigation__link"
            v-bind="linkAttrs(item)"
            :aria-current="item.current ? 'page' : undefined"
            :aria-disabled="item.disabled ? 'true' : undefined"
            @click="onLinkActivate"
          >
            <component :is="item.icon" v-if="item.icon" class="console-navigation__icon" />
            <span class="console-navigation__label">{{ item.label }}</span>
            <span v-if="item.external && !item.to" class="sr-only">(opens in new tab)</span>
            <span v-if="item.badge !== undefined" class="console-navigation__badge">{{ item.badge }}</span>
            <span class="console-navigation__tooltip" aria-hidden="true">{{ item.label }}</span>
          </component>
        </li>
      </template>
    </ul>

    <button
      type="button"
      class="console-navigation__collapse-button"
      :aria-label="toggleLabel"
      :aria-expanded="!collapsed"
      @click="toggleRail"
    >
      <BfIconChevronLeft />
    </button>
  </nav>

  <div class="console-layout__scrim" :data-drawer="open ? 'open' : undefined" @click="closeDrawer" />
</template>

<style scoped lang="scss">
  .console-navigation {
    /* Both widths live here so the toggle and the drawer read them instead of repeating
       the literals. */
    --bf-console-nav-w: 220px;
    --bf-console-nav-rail-w: 64px;

    position: relative;
    flex: 0 0 auto;
    width: var(--bf-console-nav-w);
    display: flex;
    flex-direction: column;
    background: var(--bf-white);
    border-right: 1px solid var(--bf-line);
    font-family: var(--bf-font-body);
    transition: width var(--bf-dur-base) var(--bf-ease);
  }

  .console-navigation__list {
    list-style: none;
    margin: 0;
    padding: 24px 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }

  .console-navigation__item {
    position: relative;
  }

  /* ---------- link ---------- */
  .console-navigation__link {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 8px 16px;
    border: 0;
    background: none;
    text-align: left;
    font-family: inherit;
    font-size: var(--bf-fs-sm);
    font-weight: var(--bf-fw-regular);
    line-height: 1.4;
    color: var(--bf-fg-2);
    text-decoration: none;
    cursor: pointer;
    transition:
      background var(--bf-dur-fast) var(--bf-ease),
      color var(--bf-dur-fast) var(--bf-ease);
  }

  .console-navigation__link:hover {
    background: var(--bf-grey-light);
    color: var(--bf-fg-1);
  }

  /* The list scrolls, so a spread focus ring would be clipped at the top and bottom edges —
     the ring is drawn inside the row. */
  .console-navigation__link:focus-visible {
    outline: 2px solid var(--bf-orange-dark);
    outline-offset: -2px;
  }

  /* Active hangs off aria-current, not a class — same convention as the pager and the tabs.
     The tint is the existing --bf-nav-active, already the selected-row surface for both of
     those; no new orange token. */
  .console-navigation__link[aria-current='page'] {
    background: var(--bf-nav-active);
    color: var(--bf-orange-dark);
    font-weight: var(--bf-fw-semibold);
  }

  .console-navigation__link[aria-current='page']::before {
    content: '';
    position: absolute;
    left: 0;
    top: 3px;
    bottom: 3px;
    width: 3px;
    border-radius: 0 var(--bf-r-sm) var(--bf-r-sm) 0;
    background: var(--bf-orange-dark);
  }

  .console-navigation__link[aria-disabled='true'] {
    color: var(--bf-fg-3);
    cursor: default;
    pointer-events: none;
  }

  .console-navigation__icon {
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
    color: currentColor;
  }

  .console-navigation__label {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .console-navigation__badge {
    flex: 0 0 auto;
    min-width: 20px;
    height: 18px;
    padding: 0 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--bf-r-pill);
    background: var(--bf-grey-light);
    color: var(--bf-fg-2);
    font-size: var(--bf-fs-2xs);
    font-weight: var(--bf-fw-semibold);
    letter-spacing: var(--bf-tr-nudge);
  }

  .console-navigation__link[aria-current='page'] .console-navigation__badge {
    background: var(--bf-orange-dark);
    color: var(--bf-white);
  }

  /* ---------- group divider ----------
     One markup, two treatments: the hairline is the default, --labelled-group-dividers turns the same span
     into an uppercase caption. */
  .console-navigation__group-divider {
    height: 1px;
    margin: 12px 16px;
    background: var(--bf-line);
  }

  .console-navigation__group-label {
    display: none;
  }

  .console-navigation--labelled-group-dividers .console-navigation__group-divider {
    height: auto;
    margin: 18px 0 4px;
    padding: 0 16px;
    background: none;
  }

  .console-navigation--labelled-group-dividers .console-navigation__group-label {
    display: block;
    font-size: var(--bf-fs-2xs);
    font-weight: var(--bf-fw-semibold);
    text-transform: uppercase;
    letter-spacing: var(--bf-tr-caps-md);
    color: var(--bf-fg-3);
  }

  /* ---------- rail tooltip ----------
     Hidden outright when expanded: the label is already visible, and a live tooltip element
     would be read twice. */
  .console-navigation__tooltip {
    display: none;
  }

  /* ---------- collapse toggle ----------
     Straddles the nav's right edge; the nav is the positioning context, so it needs no width. */
  .console-navigation__collapse-button {
    position: absolute;
    top: 50%;
    right: -13px;
    transform: translateY(-50%);
    width: 26px;
    height: 26px;
    padding: 0;
    display: grid;
    place-items: center;
    border-radius: var(--bf-r-pill);
    border: 1px solid var(--bf-line);
    background: var(--bf-white);
    color: var(--bf-fg-2);
    cursor: pointer;
    z-index: 3;
    transition:
      border-color var(--bf-dur-fast) var(--bf-ease),
      color var(--bf-dur-fast) var(--bf-ease),
      box-shadow var(--bf-dur-fast) var(--bf-ease);
  }

  .console-navigation__collapse-button :deep(svg) {
    width: 15px;
    height: 15px;
    transition: transform var(--bf-dur-base) var(--bf-ease);
  }

  .console-navigation__collapse-button:hover {
    border-color: var(--bf-orange-dark);
    color: var(--bf-orange-dark);
    box-shadow: 0 0 0 4px rgba(240, 141, 0, 0.14);
  }

  .console-navigation__collapse-button:focus-visible {
    outline: 2px solid var(--bf-orange-dark);
    outline-offset: 2px;
  }

  /* ---------- scrim ---------- Only ever visible below --bf-bp-console. */
  .console-layout__scrim {
    display: none;
  }

  /* ============================================================
     Rail — only above --bf-bp-console. Below it the modifier is inert, so the drawer is always
     full-label and none of these rules need undoing.
     ============================================================ */
  @container bfconsole (min-width: 861px) {
    .console-navigation[data-state='rail'] {
      width: var(--bf-console-nav-rail-w);
    }

    .console-navigation[data-state='rail'] .console-navigation__list {
      padding: 16px 0;
      overflow: visible;
    }

    .console-navigation[data-state='rail'] .console-navigation__link {
      padding: 11px 0;
      justify-content: center;
      gap: 0;
    }

    /* Visually hidden, not removed: in rail mode the accessible name still comes from the
       label, which makes the tooltip decorative. */
    .console-navigation[data-state='rail'] .console-navigation__label {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    .console-navigation[data-state='rail'] .console-navigation__badge {
      position: absolute;
      top: 6px;
      right: 12px;
      min-width: 8px;
      width: 8px;
      height: 8px;
      padding: 0;
      color: transparent;
      overflow: hidden;
      background: var(--bf-orange-dark);
    }

    .console-navigation[data-state='rail'] .console-navigation__group-divider {
      width: 24px;
      margin: 10px auto;
    }

    .console-navigation[data-state='rail'] .console-navigation__group-label {
      display: none;
    }

    .console-navigation[data-state='rail'] .console-navigation__tooltip {
      display: block;
      position: absolute;
      left: calc(100% + 12px);
      top: 50%;
      transform: translateY(-50%);
      padding: 5px 10px;
      border-radius: var(--bf-r-sm);
      background: var(--bf-deep-blue);
      color: var(--bf-fg-inverse-1);
      font-size: var(--bf-fs-xs_5);
      font-weight: var(--bf-fw-regular);
      white-space: nowrap;
      opacity: 0;
      pointer-events: none;
      z-index: 4;
      transition: opacity var(--bf-dur-fast) var(--bf-ease);
    }

    .console-navigation[data-state='rail'] .console-navigation__tooltip::before {
      content: '';
      position: absolute;
      left: -4px;
      top: 50%;
      transform: translateY(-50%);
      border: 4px solid transparent;
      border-left: 0;
      border-right-color: var(--bf-deep-blue);
    }

    .console-navigation[data-state='rail'] .console-navigation__link:hover .console-navigation__tooltip,
    .console-navigation[data-state='rail'] .console-navigation__link:focus-visible .console-navigation__tooltip {
      opacity: 1;
    }

    .console-navigation[data-state='rail'] .console-navigation__collapse-button :deep(svg) {
      transform: rotate(180deg);
    }
  }

  /* ============================================================
     Drawer — below --bf-bp-console. Rail is off, the nav leaves the flow and slides in over the
     content. Absolute inside the shell rather than fixed to the viewport: the shell already
     starts under the app header, so it needs no header-height offset and stays correct inside a
     scrolled or embedded layout.
     ============================================================ */
  @container bfconsole (max-width: 860px) {
    .console-navigation {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      width: var(--bf-console-nav-w);
      z-index: 20;
      transform: translateX(-100%);
      transition: transform var(--bf-dur-slow) var(--bf-ease);
    }

    .console-navigation[data-drawer='open'] {
      transform: none;
      box-shadow: 2px 0 18px rgba(0, 0, 0, 0.14);
    }

    /* The shell is as tall as the page it holds, so a drawer pinned to the shell's top edge opens
       off-screen on a long page: the user taps the menu, scrolls nothing, and sees a blank white
       column. The nav keeps spanning the shell — the surface is the full column, so the scrim never
       meets a seam — but the LIST sticks to the viewport and carries its own scrollbar, so the items
       are always in view and a nav taller than the screen scrolls inside itself instead of dragging
       the page. `--console-drawer-offset` is the height of a viewport-fixed app header where the
       product has one; unset means the header scrolls away and the list may use the full screen. */
    .console-navigation__list {
      position: sticky;
      top: var(--console-drawer-offset, 0px);
      flex: 0 1 auto;
      max-height: calc(100dvh - var(--console-drawer-offset, 0px));
      overscroll-behavior: contain;
    }

    .console-navigation__collapse-button {
      display: none;
    }

    .console-layout__scrim {
      display: block;
      position: absolute;
      inset: 0;
      z-index: 10;
      background: rgba(0, 0, 0, 0.35);
      opacity: 0;
      pointer-events: none;
      transition: opacity var(--bf-dur-slow) var(--bf-ease);
    }

    .console-layout__scrim[data-drawer='open'] {
      opacity: 1;
      pointer-events: auto;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .console-navigation,
    .console-navigation__collapse-button :deep(svg) {
      transition: none;
    }
  }
</style>
